import React, { useState } from "react";
import { Modal, Upload, Button } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import {AuthenticationService} from "../services/AuthenticationService";

// Same crop-then-upload mechanism as MenuItemModal's getCroppedImage —
// pulled into one shared helper since this page needs it twice (logo 1:1,
// banner 4:3) with different aspect ratios, rather than duplicating the
// whole crop pipeline inline for each.
const getCroppedImage = async (imageSrc: string, croppedAreaPixels: any): Promise<Blob> => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise(resolve => (image.onload = resolve));
    const canvas = document.createElement("canvas");
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(
        image,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0,
        croppedAreaPixels.width, croppedAreaPixels.height
    );
    return new Promise(resolve => canvas.toBlob(blob => resolve(blob!), "image/jpeg", 0.8));
};

const BRAND = "#00AF66";

interface Props {
    label:       string;
    aspect:      number;              // 1 for square logo, 4/3 for banner
    value:       string;              // current uploaded image URL
    onChange:    (url: string) => void;
    boxWidth?:   number | string;
    boxHeight?:  number;
    filePrefix?: string;              // e.g. "logo" / "banner" — used in the uploaded filename
}

const ImageCropUpload: React.FC<Props> = ({
                                              label, aspect, value, onChange,
                                              boxWidth = 160, boxHeight = 160, filePrefix = "image",
                                          }) => {
    const [cropOpen, setCropOpen] = useState(false);
    const [rawImage, setRawImage] = useState("");
    const [crop,     setCrop]     = useState({ x: 0, y: 0 });
    const [zoom,     setZoom]     = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [uploading, setUploading] = useState(false);

    const handleCropAndUpload = async () => {
        setUploading(true);
        try {
            const blob = await getCroppedImage(rawImage, croppedAreaPixels);
            const file = new File([blob], `${filePrefix}_${Date.now()}.jpg`, { type: "image/jpeg" });
            const res: any = await AuthenticationService.uploadFile(file);
            onChange(res.url);
            setCropOpen(false);
        } catch {
            // Left to the parent to surface via its own error state — this
            // component only owns the crop/upload mechanics, not messaging.
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: 13, color: "#8F9BB3", display: "block", marginBottom: 8 }}>{label}</label>
                <Upload
                    listType="picture-card"
                    showUploadList={false}
                    style={{ width: boxWidth }}
                    beforeUpload={(file) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            setRawImage(reader.result as string);
                            setCrop({ x: 0, y: 0 });
                            setZoom(1);
                            setCropOpen(true);
                        };
                        reader.readAsDataURL(file);
                        return false;
                    }}
                >
                    <div style={{
                        width: boxWidth, height: boxHeight,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        position: "relative", overflow: "hidden", borderRadius: 8,
                    }}>
                        {value ? (
                            <>
                                <img src={value} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <div style={{
                                    position: "absolute", bottom: 0, left: 0, right: 0,
                                    background: "rgba(0,0,0,0.45)", color: "#fff",
                                    fontSize: 11, textAlign: "center", padding: "6px 0",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                                }}>
                                    <EditOutlined /> Change
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: "center", color: "#BDBDBD" }}>
                                <PlusOutlined style={{ fontSize: 20 }} />
                                <div style={{ marginTop: 6, fontSize: 12 }}>Upload</div>
                            </div>
                        )}
                    </div>
                </Upload>
            </div>

            <Modal
                open={cropOpen}
                title={`Crop ${label}`}
                width={500}
                onCancel={() => setCropOpen(false)}
                footer={
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <Button onClick={() => setCropOpen(false)}>Cancel</Button>
                        <Button type="primary" loading={uploading} onClick={handleCropAndUpload} style={{ background: BRAND, borderColor: BRAND }}>
                            {uploading ? "Uploading…" : "Crop & Upload"}
                        </Button>
                    </div>
                }
            >
                <div style={{ position: "relative", height: 300, background: "#333" }}>
                    <Cropper
                        image={rawImage}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={(_:any, pixels:any) => setCroppedAreaPixels(pixels)}
                    />
                </div>
                <div style={{ marginTop: 12 }}>
                    <label style={{ fontSize: 13, color: "#888" }}>Zoom</label>
                    <input
                        type="range"
                        min={1} max={3} step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        style={{ width: "100%" }}
                    />
                </div>
            </Modal>
        </>
    );
};

export default ImageCropUpload;